import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './OrderCart.css';
import OrderCartItem from './OrderCartItem';
import { UserContext } from '../../../../../assets/contexts/userContext';
import mainApi from '../../../../../assets/api/MainApi';
import {
    ORDER_AVAILIBLE_FOR_EDIT_STATUSES,
} from '../../../../../assets/utils/constants';
import {
    deepEqual,
    parseApiError,
    parsePrice,
} from '../../../../../assets/utils/utils';
import useAutoDismissError from '../../../../../assets/hooks/useAutoDismissError';
import ErrorMessage from '../../../../ErrorMessage/ErrorMessage';
import MiniPreloader from '../../../../MiniPreloader/MiniPreloader';
import useWindowSize from '../../../../../assets/hooks/useWindowSize';
import OrderControls from '../OrderControls/OrderControls';

function getMinimalAllowedAmount(itemsForm, currentItem) {
    if (!itemsForm || itemsForm.length === 0) return 0;

    const { _id, options } = currentItem;

    if (itemsForm.length === 1) return 1;

    const othersAllZero = itemsForm
        .filter((it) => !(it._id === _id && deepEqual(it.options, options)))
        .every((it) => Number(it.amount) === 0);

    if (othersAllZero) return 1;

    return 0;
}

function OrderCart({
    order,
    config,
    items,
    setItems,
    setOrder,
}) {
    const { user } = useContext(UserContext);
    const [draftItems, setDraftItems] = useState(items || []);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const [error, showError] = useAutoDismissError();
    const { width } = useWindowSize()
    const headerSentinelRef = useRef(null);

    const canEdit = useMemo(
        () => !!order && ((order?.status === 'paid' && order?.payment_type === 'yookassa') || (order?.status === 'new' && order?.payment_type === 'manager')),
        [order],
    );
    const shouldAutostartEdit = useMemo(
        () => order?.status === 'paid' && order?.payment_type === 'yookassa',
        [order?.payment_type, order?.status],
    );
    const discount = Number(order?.discount || 0);

    useEffect(() => {
        setDraftItems(items || []);
    }, [items]);

    useEffect(() => {
        if (shouldAutostartEdit && canEdit) {
            setIsEditing(true);
        } else if (!canEdit) {
            setIsEditing(false);
        }
    }, [canEdit, shouldAutostartEdit]);

    const currentItems = useMemo(
        () => (isEditing ? draftItems : items) || [],
        [draftItems, isEditing, items],
    );

    const changedItems = useMemo(() => {
        if (!draftItems || !items) return [];
        return draftItems.filter((item) => {
            const originalItem = items.find(
                (obj) =>
                    obj._id === item._id &&
                    deepEqual(obj.options, item.options),
            );
            if (!originalItem) return true;
            return Number(item.amount) !== Number(originalItem.amount);
        });
    }, [draftItems, items]);

    const summary = useMemo(() => {
        if (!currentItems) return { total: 0, count: 0 };
        const total = currentItems.reduce((acc, item) => {
            const price = discount
                ? Number(item.price) - (Number(item.price) / 100) * discount
                : Number(item.price);
            return acc + price * Number(item.amount || 0);
        }, 0);
        const count = currentItems.reduce(
            (acc, item) => acc + Number(item.amount || 0),
            0,
        );
        return { total, count };
    }, [currentItems, discount]);

    function handleMinus({ _id, options }) {
        setDraftItems((prev) => {
            if (!prev) return prev;
            const minimalValue = getMinimalAllowedAmount(prev, { _id, options });

            return prev.map((item) => {
                if (item._id === _id && deepEqual(item.options, options)) {
                    return {
                        ...item,
                        amount: `${Math.max(minimalValue, Number(item.amount) - 1)}`,
                    };
                }
                return item;
            });
        });
    }

    function handlePlus({ _id, options }) {
        setDraftItems((prev) => {
            if (!prev) return prev;
            return prev.map((item) => {
                if (item._id === _id && deepEqual(item.options, options)) {
                    const originalItem = items?.find(
                        (obj) => obj._id === item._id && deepEqual(obj.options, item.options),
                    );
                    const maxAmount = Number(originalItem?.amount || item.amount);
                    return {
                        ...item,
                        amount: `${Math.min(maxAmount, Number(item.amount) + 1)}`,
                    };
                }
                return item;
            });
        });
    }

    function handleReset() {
        setDraftItems(items || []);
    }

    function handleSave() {
        if (!canEdit || !changedItems.length || isSaving) return;

        const shop_id = user?.default_shop?._id;
        if (!shop_id || !order?._id) return;

        const itemsToUpdate = changedItems.map((item) => ({
            _id: item._id,
            amount: `${item.amount}`,
            options: item.options,
        }));

        setIsSaving(true);
        mainApi
            .updateOrderItems({
                shop_id,
                data: itemsToUpdate.map((item) => ({
                    _id: item._id,
                    amount: `${item.amount}`,
                })),
                order_id: order._id,
                payment_type: order.payment_type,
            })
            .then((res) => {
                const applyChanges = (source = []) => {
                    const next = [...(source || [])];

                    itemsToUpdate.forEach((updatedItem) => {
                        const index = next.findIndex(
                            (it) =>
                                it._id === updatedItem._id &&
                                deepEqual(it.options, updatedItem.options),
                        );

                        if (Number(updatedItem.amount) === 0) {
                            if (index !== -1) {
                                next.splice(index, 1);
                            }
                        } else if (index !== -1) {
                            next[index] = {
                                ...next[index],
                                amount: `${updatedItem.amount}`,
                            };
                        }
                    });

                    return next;
                };

                if (setItems) {
                    setItems((prev) => applyChanges(prev));
                } else {
                    setDraftItems((prev) => applyChanges(prev));
                }

                if (setOrder) {
                    const shopId = user?.default_shop?._id;
                    if (shopId && order?._id) {
                        mainApi
                            .getExactOrder({ shop_id: shopId, _id: order._id })
                            .then((freshOrder) => {
                                setOrder(freshOrder);
                            })
                            .catch((err) => {
                                showError(parseApiError(err));
                            });
                    }
                }
                setIsEditing(shouldAutostartEdit);
            })
            .catch((err) => {
                showError(err?.message || 'Не удалось сохранить изменения корзины');
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    useEffect(() => {
        if (!canEdit || width > 780) {
            setIsHeaderSticky(false);
            return undefined;
        }

        const sentinel = headerSentinelRef.current;
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            setIsHeaderSticky(!entry.isIntersecting);
        });

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [canEdit, width]);

    const headerClassName = [
        'order-cart__header',
        canEdit ? 'order-cart__header_can-edit' : '',
        isHeaderSticky ? 'order-cart__header_sticky' : '',
    ].filter(Boolean).join(' ');


    return (
        <div className="order-cart">
            <div className="order-cart__header-sentinel" ref={headerSentinelRef} />
            <div className={headerClassName}>
                {width <= 780 ?
                    <OrderControls
                        order={order}
                        config={config}
                        items={items}
                        setOrder={setOrder}
                    />
                    : null}
                <div className='order-cart__actions-wrap'>
                    <div className="order-cart__title-wrap">
                        <p className="order-cart__title">Корзина</p>
                        {isEditing ? (
                            <span className="order-cart__pill order-cart__pill_edit">
                                Режим редактирования
                            </span>
                        ) : (
                            <span className="order-cart__pill">
                                {summary.count} позиций · {parsePrice(summary.total, 'RUB')}
                            </span>
                        )}
                    </div>
                    {canEdit ? (
                        <div className="order-cart__actions">
                            {!isEditing ? (
                                <button
                                    type="button"
                                    className="order-cart__btn order-cart__btn_primary"
                                    onClick={() => {
                                        setIsEditing(true);
                                        setDraftItems(items || []);
                                    }}
                                >
                                    Изменить корзину
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="order-cart__btn"
                                        onClick={handleReset}
                                        disabled={!changedItems.length || isSaving}
                                    >
                                        Сбросить
                                    </button>
                                    <button
                                        type="button"
                                        className="order-cart__btn order-cart__btn_primary"
                                        onClick={handleSave}
                                        disabled={!changedItems.length || isSaving}
                                    >
                                        {isSaving ? <MiniPreloader /> : 'Сохранить'}
                                    </button>
                                </>
                            )}
                        </div>
                    ) : null}
                </div>

            </div>

            {/* {isEditing ? (
                <p className="order-cart__helper">
                    Внесите корректировки по количеству. Минимум одна позиция должна остаться в заказе.
                </p>
            ) : null} */}

            {isEditing &&
                <div className="order-cart__summary">
                    <div className="order-cart__summary-item">
                        <span className="order-cart__summary-label">Позиции</span>
                        <span className="order-cart__summary-value">
                            {summary.count}
                        </span>
                    </div>
                    <div className="order-cart__summary-item">
                        <span className="order-cart__summary-label">Сумма</span>
                        <span className="order-cart__summary-value">
                            {parsePrice(summary.total, 'RUB')}
                        </span>
                    </div>
                    {order.promo_code && discount ?
                        <div className="order-cart__summary-item">
                            <span className="order-cart__summary-label">Скидка</span>
                            <span className="order-cart__summary-value">
                                {discount ? `-${discount}%` : '—'} ({order.promo_code})
                            </span>
                        </div>
                        : null}

                </div>
            }

            <div className="order-cart__items">
                {currentItems && currentItems.length ? (
                    currentItems.map((item) => {
                        const price = discount
                            ? Number(item.price) - (Number(item.price) / 100) * discount
                            : Number(item.price);
                        const minimalValue = getMinimalAllowedAmount(
                            draftItems,
                            item,
                        );
                        const originalItem = items?.find(
                            (obj) =>
                                obj._id === item._id &&
                                deepEqual(obj.options, item.options),
                        );
                        const maxAmount = Number(originalItem?.amount || item.amount);
                        const isChanged = isEditing && changedItems.some(
                            (changed) =>
                                changed._id === item._id &&
                                deepEqual(changed.options, item.options),
                        );
                        const isRemoved = Number(item.amount) === 0;

                        return (
                            <OrderCartItem
                                key={`${item._id}-${JSON.stringify(item.options || [])}`}
                                item={item}
                                amount={item.amount}
                                price={price}
                                totalPrice={price * Number(item.amount || 0)}
                                discount={discount}
                                onMinus={() => handleMinus({ _id: item._id, options: item.options })}
                                onPlus={() => handlePlus({ _id: item._id, options: item.options })}
                                isEditing={isEditing}
                                minimalValue={minimalValue}
                                maxAmount={maxAmount}
                                isChanged={isChanged}
                                isRemoved={isRemoved}
                            />
                        );
                    })
                ) : (
                    <div className="order-cart__empty">
                        Товары в заказе отсутствуют
                    </div>
                )}
            </div>

            <ErrorMessage error={error} />
        </div>
    );
}

export default OrderCart;
